import { Box, Button, Drawer, FormControl, InputLabel, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { format } from "date-fns";
import { useSnackbar } from "notistack";
import React from "react";
import { ApiError } from "../../Api/ApiError";
import { addLessonStudio, deleteLessonStudio, editLessonStudio, getLessonStudioList, LessonStudioList, LessonStudioListType } from "../../Api/OnlineLesson";
import { LessonStudio } from "../../Objects/OnlineLesson";
import { StoreObjectStore } from "../../ObjectStore/StoreObjectStore";
import CategoryTitle from "../atoms/CategoryTitle";
import EditOnlineLessonStudioForm, { EditOnlineLessonStudioFormData } from "../molecules/EditOnlineLessonStudioForm";

const LessonStudioListTypeSelectText = {
    all: "全て表示",
    apply: "適用中のみ表示",
    default: "削除したものを表示しない"
}

const StoreDetailOnlineLesson: React.FC = () => {
    const DISPLAY_ROWS = 6
    let { storeObjectState } = React.useContext(StoreObjectStore)
    let { enqueueSnackbar } = useSnackbar()
    let [openEditOnlineLessonStudio, setOpenEditOnlineLessonStudio] = React.useState(false)
    let [editOnlineLessonStudioFormData, setEditOnlineLessonStudioFormData] = React.useState<EditOnlineLessonStudioFormData>({})

    let [lessonStudioList, setLessonStudioList] = React.useState<LessonStudioList>()
    let [offset, setOffset] = React.useState(0)
    let [lessonStudioListType, setLessonStudioListType] = React.useState<LessonStudioListType>(LessonStudioListType.default)

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    /**
     * 利用可能スタジオ一覧を取得する
     */
    let loadLessonStudioList = React.useCallback(() => {
        if (storeObjectState.storeData && storeObjectState.storeData.str_id) {
            getLessonStudioList({
                limit: DISPLAY_ROWS,
                offset: offset,
                str_id: storeObjectState.storeData.str_id,
                type: lessonStudioListType
            }, {signal: abortController.signal}).then(_lessonStudioList => {
                setLessonStudioList(_lessonStudioList)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, DISPLAY_ROWS, offset, storeObjectState.storeData, lessonStudioListType, enqueueSnackbar])

    /**
     * 利用可能スタジオを追加する
     */
    let doSave = React.useCallback(() => {
        if (editOnlineLessonStudioFormData && editOnlineLessonStudioFormData.std_id) {
            if (editOnlineLessonStudioFormData.lstd_id) {
                editLessonStudio({
                    lstd_id: editOnlineLessonStudioFormData.lstd_id,
                    end_apply: editOnlineLessonStudioFormData.end_apply
                }, {signal: abortController.signal}).then(() => {
                    enqueueSnackbar("保存しました", { variant: 'success' })
                    setOpenEditOnlineLessonStudio(false)
                    loadLessonStudioList()
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })

            } else {
                addLessonStudio({
                    std_id: editOnlineLessonStudioFormData.std_id,
                    start_apply: editOnlineLessonStudioFormData.start_apply,
                    end_apply: editOnlineLessonStudioFormData.end_apply,
                }, {signal: abortController.signal}).then(lstd_id => {
                    enqueueSnackbar("保存しました", { variant: 'success' })
                    setOpenEditOnlineLessonStudio(false)
                    loadLessonStudioList()
                }).catch((e: any) => {
                    if( e.__CANCEL__ ) {
                        console.info("canceled")
                    } else if (e instanceof ApiError) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    } else if (e instanceof Error) {
                        enqueueSnackbar(e.message, { variant: "error" })
                    }else {
                        enqueueSnackbar("通信に失敗しました", { variant: "error" })
                    }
                })
            }
        }
    }, [abortController, enqueueSnackbar, editOnlineLessonStudioFormData, loadLessonStudioList])

    let doDelete = React.useCallback(() => {
        if (editOnlineLessonStudioFormData && editOnlineLessonStudioFormData.lstd_id) {
            deleteLessonStudio({
                lstd_id: editOnlineLessonStudioFormData.lstd_id
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("削除しました", { variant: 'success' })
                setOpenEditOnlineLessonStudio(false)
                loadLessonStudioList()
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, editOnlineLessonStudioFormData, enqueueSnackbar, loadLessonStudioList])

    React.useEffect(() => {
        loadLessonStudioList()
    }, [loadLessonStudioList])

    return (
        <Paper sx={{ mt: 2 }}>
            <CategoryTitle
                text="オンラインレッスン設定"
            />
            <Box sx={{
                display: 'flex',
                justifyContent: 'right',
                padding: '10px'
            }}>
                <FormControl
                    size="small"
                    sx={{
                        mr: 0.5
                    }}
                >
                    <InputLabel id="lesson-studio-list-type">表示</InputLabel>
                    <Select
                        labelId="lesson-studio-list-type"
                        label="表示"
                        value={lessonStudioListType}
                        onChange={(e: SelectChangeEvent<any>) => {
                            setLessonStudioListType(e.target.value)
                            loadLessonStudioList()
                        }}
                    >
                        <MenuItem value={LessonStudioListType.all} key={LessonStudioListType.all}>{LessonStudioListTypeSelectText.all}</MenuItem>
                        <MenuItem value={LessonStudioListType.apply} key={LessonStudioListType.apply}>{LessonStudioListTypeSelectText.apply}</MenuItem>
                        <MenuItem value={LessonStudioListType.default} key={LessonStudioListType.default}>{LessonStudioListTypeSelectText.default}</MenuItem>
                   </Select>
                </FormControl>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                        setOpenEditOnlineLessonStudio(true)
                    }}
                >利用可能スタジオを追加する</Button>
            </Box>
            {lessonStudioList &&
                <Box sx={{ p: 2 }}>
                    <TableContainer component={Paper} >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>id</TableCell>
                                    <TableCell>スタジオ</TableCell>
                                    <TableCell>適用開始日</TableCell>
                                    <TableCell>適用終了日</TableCell>
                                    <TableCell>追加日</TableCell>
                                    <TableCell>削除日</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lessonStudioList.list.map((lessonStudio: LessonStudio) => (
                                    <TableRow
                                        key={lessonStudio.lstd_id}
                                        onClick={() => {
                                            if(lessonStudio.delete_at) {
                                                return
                                            }
                                            let formData: EditOnlineLessonStudioFormData = {
                                                lstd_id: lessonStudio.lstd_id,
                                                std_id: lessonStudio.std_id,
                                                start_apply: lessonStudio.start_apply,
                                                end_apply: lessonStudio.end_apply
                                            }
                                            setEditOnlineLessonStudioFormData(formData)
                                            setOpenEditOnlineLessonStudio(true)
                                        }}
                                        hover={lessonStudio.delete_at ? false : true}
                                        sx={{
                                            bgcolor: lessonStudio.delete_at ? "grey.200" : "background.paper"
                                        }}
                                    >
                                        <TableCell>{lessonStudio.lstd_id}</TableCell>
                                        <TableCell>{lessonStudio.std_name}{lessonStudio.std_area ? "/" + lessonStudio.std_area : ""}</TableCell>
                                        <TableCell>{lessonStudio.start_apply ? format(lessonStudio.start_apply, "yyyy/MM/dd") : ""}</TableCell>
                                        <TableCell>{lessonStudio.end_apply ? format(lessonStudio.end_apply, "yyyy/MM/dd") : ""}</TableCell>
                                        <TableCell>{lessonStudio.create_at ? format(lessonStudio.create_at, "yyyy/MM/dd HH:mm:ss") : ""}</TableCell>
                                        <TableCell>{lessonStudio.delete_at ? format(lessonStudio.delete_at, "yyyy/MM/dd HH:mm:ss") : ""}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{
                        padding: "10px",
                        display: "flex",
                        justifyContent: "center",
                    }}>
                        <Pagination count={lessonStudioList.pagenum} onChange={(e, page) => {
                            setOffset((page - 1) * DISPLAY_ROWS)
                        }} variant="outlined" />
                    </Box>
                </Box>
            }
            <Drawer
                anchor="bottom"
                open={openEditOnlineLessonStudio}
                onClose={() => {
                    setOpenEditOnlineLessonStudio(false)
                }}
                sx={{
                    zIndex: 1210,
                }}
                PaperProps={{
                    sx: {
                        minHeight: '50%',
                        borderRadius: "1em 1em 0 0",
                    }
                }}
                SlideProps={{
                    onExited: () => {
                        setEditOnlineLessonStudioFormData({})
                    },
                }}
            >
                <Box sx={{ padding: "14px" }}>
                    <CategoryTitle
                        text={editOnlineLessonStudioFormData.lstd_id ? "編集" : "追加"}
                        lined
                        HeaderItem={
                            <Box sx={{
                                textAlign: "right"
                            }}>
                                {editOnlineLessonStudioFormData.lstd_id &&
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            doDelete()
                                        }}
                                        sx={{ mx: 0.5 }}
                                        color="error"
                                    >削除</Button>
                                }
                                <Button
                                    variant="contained"
                                    onClick={() => {
                                        doSave()
                                    }}
                                    sx={{ mx: 0.5 }}
                                >保存</Button>
                            </Box>}
                    />
                    <Box sx={{pt: 1}}>
                        {storeObjectState.studioList &&
                            <EditOnlineLessonStudioForm
                                editOnlineLessonStudioFormData={editOnlineLessonStudioFormData}
                                studioDataList={storeObjectState.studioList}
                                onChange={(formData: EditOnlineLessonStudioFormData) => {
                                    setEditOnlineLessonStudioFormData(formData)
                                }}
                            />
                        }
                    </Box>
                </Box>
            </Drawer>
        </Paper>
    );
}

export default StoreDetailOnlineLesson